import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import IncomingCallModal from "../messenger/IncomingCallModal";
import OutgoingCallModal from "../messenger/OutgoingCallModal";
import VideoCallWindow from "../messenger/VideoCallWindow";
import ConversationsList from "../messenger/ConversationsList";
import ChatWindow from "../messenger/ChatWindow";
import DeleteGroupModal from "../messenger/ChatWindow/DeleteGroupModal";
import { messengerService } from "../../functions/messenger";
import { useSocketContext } from "../../context/SocketContext";
import { getCustomLists } from "../../functions/customLists";
import "./style.css";

export default function MessengerSidebar({ isOpen, onClose, isMobile = false, pendingParticipantId = null, onPendingHandled = () => {} }) {

  const navigate = useNavigate();
  const location = useLocation();
  const isOnProfileRoute = location.pathname === '/profile' || location.pathname.startsWith('/profile/');
  const { user } = useSelector((state) => ({ ...state }));
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [outgoingCall, setOutgoingCall] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [groups, setGroups] = useState([]);
  // A left/removed group awaiting a "Delete Group" confirmation in the Groups list.
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isDeletingLeftGroup, setIsDeletingLeftGroup] = useState(false);
  const [activeTab, setActiveTab] = useState('chats'); // 'chats' or 'groups'
  const [groupUnreadCounts, setGroupUnreadCounts] = useState({}); // Track unread counts for groups
  const [groupLastActivity, setGroupLastActivity] = useState({}); // Track last activity for groups
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showStarredMoreMenu, setShowStarredMoreMenu] = useState(false);
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [showArchivedConversations, setShowArchivedConversations] = useState(false);
  const [starredMessages, setStarredMessages] = useState([]);
  const [showStarredMessages, setShowStarredMessages] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [pendingHighlightMessageId, setPendingHighlightMessageId] = useState(null);
  const [isProfileTransitioning, setIsProfileTransitioning] = useState(false);
  // Hide messenger only when the user opened a profile from an active chat — not when
  // they explicitly open messenger / Message from the profile page itself.
  const [hideMessengerWhileOnProfile, setHideMessengerWhileOnProfile] = useState(false);
  const messagesEndRef = useRef(null);
  const moreMenuRef = useRef(null);
  const starredMoreMenuRef = useRef(null);
  const sidebarContainerRef = useRef(null);
  const skipAutoScrollRef = useRef(false);

  // Use the socket context
  const { socket, isConnected } = useSocketContext();

  // Initialize messenger when opened
  useEffect(() => {
    if (isOpen && user?.token) {
      initializeMessenger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // Leaving profile after opening it from chat:
  // - Home → close messenger (do not overlay the feed)
  // - Other pages / back → clear the hide flag so messenger can show again
  useEffect(() => {
    if (isOnProfileRoute) return undefined;

    const isHome =
      location.pathname === "/" || location.pathname === "/home";

    if (hideMessengerWhileOnProfile && isHome) {
      onClose();
    }
    setHideMessengerWhileOnProfile(false);

    if (isProfileTransitioning) {
      const t = setTimeout(() => setIsProfileTransitioning(false), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [
    isOnProfileRoute,
    isProfileTransitioning,
    hideMessengerWhileOnProfile,
    location.pathname,
    onClose,
  ]);

  // Show messenger when opened explicitly from profile (Message) or header toggle.
  // Must clear profile_transitioning too — that class keeps opacity:0 / pointer-events:none
  // while still on /profile/*, which blocked Message → 1:1 chat on mobile.
  useEffect(() => {
    const revealMessenger = () => {
      setHideMessengerWhileOnProfile(false);
      setIsProfileTransitioning(false);
    };

    window.addEventListener('openMessenger', revealMessenger);
    window.addEventListener('toggleMessenger', revealMessenger);

    return () => {
      window.removeEventListener('openMessenger', revealMessenger);
      window.removeEventListener('toggleMessenger', revealMessenger);
    };
  }, []);

  // After page refresh: restore open chat from sessionStorage so we stay in messages_container
  useEffect(() => {
    if (!isOpen || !user?.token || conversations.length === 0 || selectedConversation) return;
    let storedId;
    try {
      storedId = sessionStorage.getItem('messenger_active_conversation_id');
    } catch (e) { return; }
    if (!storedId) return;
    const conv = conversations.find((c) => c._id === storedId);
    if (conv) {
      handleConversationSelect(conv);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.token, conversations, selectedConversation]);

  // If we receive a pending participant to open, start/open that conversation
  useEffect(() => {
    if (isOpen && pendingParticipantId && user?.token) {
      setHideMessengerWhileOnProfile(false);
      setIsProfileTransitioning(false);
      (async () => {
        try {
          await handleStartNewConversation(pendingParticipantId);
        } catch (e) {
          console.error('Failed to open conversation for pending participant:', e);
        } finally {
          onPendingHandled();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pendingParticipantId, user?.token]);

  // Auto full screen on mobile when messenger opens
  useEffect(() => {
    if (isOpen && isMobile) {
      setIsFullScreen(true);
      // Prevent body scroll when messenger is open on mobile
      document.body.style.overflow = 'hidden';
    } else if (!isOpen) {
      setIsFullScreen(false);
      // Restore body scroll when messenger is closed
      document.body.style.overflow = 'auto';
    }

    // Cleanup function to restore body scroll
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, isMobile]);

  // Socket event listeners
  useEffect(() => {
    if (!socket || !isOpen || !isConnected) {
      return;
    }

    socket.emit('join', user.id);

    // Message events
    socket.on('new_message', handleNewMessage);
    socket.on('message_reaction', handleMessageReaction);
    socket.on('message_deleted', handleMessageDeleted);
    socket.on('message_edited', handleMessageEdited);
    socket.on('messages_read', handleMessagesRead);

    // Typing events
    socket.on('user-typing', handleUserTyping);

    // Call events
    socket.on('incoming_call', handleIncomingCall);
    socket.on('call_answered', handleCallAnswered);
    socket.on('call_declined', handleCallDeclined);
    socket.on('call_ended', handleCallEnded);
    socket.on('call_canceled', handleCallCanceled);
    
    // Online/Offline status events
    socket.on('user-online', handleUserOnline);
    socket.on('user-offline', handleUserOffline);
    socket.on('online-users-list', handleOnlineUsersList);

    // Group membership events
    socket.on('group_member_left', handleGroupMemberLeft);
    socket.on('group_member_added', handleGroupMemberAdded);
    socket.on('group_member_removed', handleGroupMemberRemoved);
    socket.on('group_disbanded', handleGroupDisbanded);

    return () => {

      socket.off('new_message');
      socket.off('message_reaction');
      socket.off('message_deleted');
      socket.off('message_edited');
      socket.off('messages_read');
      socket.off('user-typing');
      socket.off('incoming_call');
      socket.off('call_answered');
      socket.off('call_declined');
      socket.off('call_ended');
      socket.off('call_canceled');
      socket.off('user-online');
      socket.off('user-offline');
      socket.off('online-users-list');
      socket.off('group_member_left');
      socket.off('group_member_added');
      socket.off('group_member_removed');
      socket.off('group_disbanded');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, user, isOpen]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (skipAutoScrollRef.current) return;
    scrollToBottom();
  }, [messages]);

  // Auto-refresh conversations list to update unread counts (polling fallback)
  useEffect(() => {
    if (!user?.token || !isOpen) {
      return;
    }

    let pollingInterval;

    const pollForConversations = async () => {
      try {
        const conversationsData = await messengerService.getConversations(user?.token);
        
        // Update conversations list with fresh data from server
        // This ensures unread counts are always accurate
        setConversations(prev => {
          // Merge new data with existing, but always use server's unreadCount
          const updated = conversationsData.map(newConv => {
            // If this is the currently selected conversation, preserve some state but use server's unreadCount
            if (selectedConversation && newConv._id === selectedConversation._id) {
              return { 
                ...newConv, 
                // Keep selected conversation reference but use server's unreadCount
                unreadCount: newConv.unreadCount || 0
              };
            }
            // Always use server's data for unread counts
            return newConv;
          });
          
          return updated;
        });

        // Keep selectedConversation in sync with server-side userLeft so
        // the chat flips to read-only when the user leaves on another tab.
        if (selectedConversation) {
          const fresh = conversationsData.find(
            (c) => c._id === selectedConversation._id
          );
          if (fresh && fresh.userLeft !== selectedConversation.userLeft) {
            setSelectedConversation((prev) =>
              prev && prev._id === fresh._id
                ? {
                    ...prev,
                    userLeft: fresh.userLeft,
                    leftAt: fresh.leftAt,
                    leftMembers: fresh.leftMembers || prev.leftMembers,
                    participants: fresh.participants || prev.participants,
                  }
                : prev
            );
          }
        }
      } catch (error) {
        console.error("Error polling for conversations:", error);
      }
    };

    // Poll every 5 seconds to refresh conversations and unread counts
    pollingInterval = setInterval(pollForConversations, 5000);

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, isOpen, selectedConversation?._id]);

  // Auto-refresh messages when conversation is open (polling fallback)
  useEffect(() => {
    if (!selectedConversation || !user?.token || !isOpen) {
      return;
    }

    let pollingInterval;

    const pollForNewMessages = async () => {
      try {
        const newMessages = await messengerService.getMessages(
          selectedConversation._id,
          user?.token
        );

        // Use functional update to get latest messages state and check for new messages
        setMessages(prev => {
          // Find messages that are new (not in current messages)
          const currentMessageIds = new Set(prev.map(msg => msg._id));
          const newMessagesToAdd = newMessages.filter(
            msg => !currentMessageIds.has(msg._id)
          );

          if (newMessagesToAdd.length > 0) {
            // Mark as read if new messages were added (async, don't await)
            messengerService.markAsRead(selectedConversation._id, user?.token).catch(err => {
              console.error("Error marking messages as read:", err);
            });
            return [...prev, ...newMessagesToAdd];
          }

          return prev;
        });
      } catch (error) {
        console.error("Error polling for new messages:", error);
      }
    };

    // Poll every 3 seconds
    pollingInterval = setInterval(pollForNewMessages, 3000);

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?._id, user?.token, isOpen]);

  const initializeMessenger = async () => {
    try {
      setLoading(true);
      const [conversationsData, groupsData, blockedUsersData] = await Promise.all([
        messengerService.getConversations(user?.token),
        getCustomLists(user?.token),
        messengerService.getBlockedUsers(user?.token).catch(() => [])
      ]);
      setConversations(conversationsData);
      setBlockedUsers(Array.isArray(blockedUsersData) ? blockedUsersData : []);



      if (groupsData.status === "ok") {
        const allGroups = [
          ...(groupsData.data.ownedLists || []),
          ...(groupsData.data.memberLists || [])
        ];

        setGroups(allGroups);

        // Calculate unread counts for group conversations
        await calculateGroupUnreadCounts(allGroups);
      } else {

      }
    } catch (error) {
      console.error("Error initializing messenger:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate unread message counts for groups
  const calculateGroupUnreadCounts = async (groupsList) => {
    try {


      const unreadCounts = {};

      for (const group of groupsList) {


        // Find the conversation for this group
        let groupConversation = conversations.find(conv =>
          conv.type === 'group' && conv.name === group.name
        );



        // If no conversation exists, try to create one
        if (!groupConversation) {

          try {
            const memberIds = group.members ? group.members.map(member => member._id || member.id).filter(Boolean) : [];

            // Add the group owner to the participants if not already included
            const ownerId = group.owner?._id || group.owner?.id || group.owner;
            if (ownerId && !memberIds.includes(ownerId)) {
              memberIds.push(ownerId);
            }

            groupConversation = await messengerService.getOrCreateConversation({
              groupId: group.id,
              type: 'group',
              name: group.name,
              participants: memberIds
            }, user?.token);

          } catch (error) {
            console.error("❌ Failed to create conversation for group:", group.name, error);
          }
        }

        if (groupConversation) {
          // Get unread count for this conversation
          const unreadCount = await messengerService.getUnreadCount(groupConversation._id, user?.token);
          unreadCounts[group.id] = unreadCount;
        } else {
          unreadCounts[group.id] = 0;
        }
      }


      setGroupUnreadCounts(unreadCounts);
    } catch (error) {
      console.error("❌ Error calculating group unread counts:", error);
    }
  };

  // Recalculate group unread counts when conversations change
  useEffect(() => {
    if (groups.length > 0) {

      calculateGroupUnreadCounts(groups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, conversations, user?.token]); // Added user?.token as dependency with null check



  const handleConversationSelect = async (conversation) => {
    try {
      setSelectedConversation(conversation);
      window.activeConversationId = conversation._id;
      try {
        window.__messengerChatOpen = true; // Block feed/page refresh while in chat
      } catch (e) { /* ignore */ }
      // Persist so after page refresh we stay in this chat (messages_container)
      try {
        sessionStorage.setItem('messenger_active_conversation_id', conversation._id);
      } catch (e) { /* ignore */ }

      // Update local conversation unreadCount immediately for better UX
      setConversations(prev => prev.map(conv =>
        conv._id === conversation._id ? { ...conv, unreadCount: 0 } : conv
      ));

      // Notify MessageNotificationContext immediately
      window.dispatchEvent(new CustomEvent('messagesReadLocal', {
        detail: { conversationId: conversation._id }
      }));

      const messagesData = await messengerService.getMessages(conversation._id, user?.token);
      setMessages(messagesData);

      // Restore previous scroll position if present (e.g. returning from profile)
      try {
        const restoreKey = `messenger_scroll_${conversation._id}`;
        const restoreValue = sessionStorage.getItem(restoreKey);
        if (restoreValue !== null) {
          skipAutoScrollRef.current = true;
          const nextTop = Number(restoreValue) || 0;
          requestAnimationFrame(() => {
            const scroller = sidebarContainerRef.current?.querySelector('.messages_container');
            if (scroller) scroller.scrollTop = nextTop;
            sessionStorage.removeItem(restoreKey);
            skipAutoScrollRef.current = false;
          });
        }
      } catch (e) {
        skipAutoScrollRef.current = false;
      }

      // Mark as read on the server
      try {
        await messengerService.markAsRead(conversation._id, user?.token);
        // console.log(`✅ Messages marked as read for conversation: ${conversation._id}`);
      } catch (markReadError) {
        console.error("❌ Error marking messages as read:", markReadError);
        // If marking as read fails, we should still show the conversation
        // but the unread count might not be accurate
      }

    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };

  const handleSendMessage = async (messageData) => {
    try {
      const newMessage = await messengerService.sendMessage({
        ...messageData,
        conversationId: selectedConversation._id,
      }, user?.token);

      setMessages(prev => [...prev, newMessage]);
      upsertConversationInList(selectedConversation._id, newMessage);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleSendMedia = async (files) => {
    try {
      const formData = new FormData();
      formData.append('conversationId', selectedConversation._id);

      for (let i = 0; i < files.length; i++) {
        formData.append('media', files[i]);
      }

      const newMessage = await messengerService.sendMediaMessage(formData, user?.token);
      setMessages(prev => [...prev, newMessage]);
      upsertConversationInList(selectedConversation._id, newMessage);
    } catch (error) {
      console.error("Error sending media:", error);
    }
  };

  const handleEditMessage = async (messageId, text) => {
    try {
      const updatedMessage = await messengerService.editMessage(messageId, text, user?.token);
      setMessages(prev => prev.map(msg => (
        msg._id === updatedMessage._id ? { ...msg, ...updatedMessage } : msg
      )));
      if (selectedConversation?._id) {
        upsertConversationInList(selectedConversation._id, updatedMessage);
      }
    } catch (error) {
      console.error("Error editing message:", error);
    }
  };

  const handleForwardMessage = async (message, conversationIds) => {
    try {
      const response = await messengerService.forwardMessage(message._id, conversationIds, user?.token);
      const createdMessages = response?.createdMessages || [];

      createdMessages.forEach(({ conversationId, message: createdMessage }) => {
        if (selectedConversation && selectedConversation._id === conversationId) {
          setMessages((prev) => {
            if (prev.some((m) => m._id === createdMessage._id)) return prev;
            return [...prev, createdMessage];
          });
        }
        upsertConversationInList(conversationId, createdMessage);
      });
    } catch (error) {
      console.error("Error forwarding message:", error);
      throw error;
    }
  };

  const handleNewMessage = ({ message, conversationId }) => {
    // Ignore live messages for groups the user has left — their read-only
    // history must stay frozen at the moment they left.
    const targetConv = (selectedConversation && selectedConversation._id === conversationId)
      ? selectedConversation
      : conversations.find((c) => c._id === conversationId);
    if (targetConv?.userLeft) {
      return;
    }

    const isConversationMatch = selectedConversation && selectedConversation._id === conversationId;

    if (selectedConversation && isConversationMatch) {

      // Ensure message has the correct structure
      const processedMessage = {
        _id: message._id,
        content: message.content,
        sender: message.sender,
        type: message.type,
        createdAt: message.createdAt,
        conversation: message.conversation,
        ...message
      };

      // Ensure text messages have proper content structure
      if (processedMessage.type === 'text' && typeof processedMessage.content === 'string') {
        processedMessage.content = { text: processedMessage.content };
      }

      // Handle different content types
      if (processedMessage.type === 'text' && !processedMessage.content.text) {
        processedMessage.content = { text: processedMessage.content };
      }



      setMessages(prev => {
        // Check if message already exists to prevent duplicates
        const messageExists = prev.some(msg => msg._id === processedMessage._id);
        if (messageExists) {
          return prev;
        }

        const newMessages = [...prev, processedMessage];
        return newMessages;
      });

      // Mark as read if conversation is open
      messengerService.markAsRead(conversationId, user?.token);
    }

    // Update conversation in list
    upsertConversationInList(conversationId, message);

    // If the incoming message is from another user and the conversation is not open, increment unreadCount
    if (message.sender._id !== user?.id && !(selectedConversation && selectedConversation._id === conversationId)) {
      setConversations(prev => prev.map(conv => conv._id === conversationId ? { ...conv, unreadCount: (conv.unreadCount || 0) + 1 } : conv));
    }

    // Update group unread counts and last activity if this is a group conversation
    const conversation = conversations.find(conv => conv._id === conversationId);
    if (conversation && conversation.type === 'group') {
      // Find the corresponding group
      const group = groups.find(g => g.name === conversation.name);
      if (group) {
        // Update last activity for the group
        setGroupLastActivity(prev => ({
          ...prev,
          [group.id]: new Date()
        }));

        // Update unread count if message is from another user
        if (message.sender._id !== user?.id) {
          setGroupUnreadCounts(prev => ({
            ...prev,
            [group.id]: (prev[group.id] || 0) + 1
          }));
        }
      }
    }
  };

  const handleMessageReaction = ({ messageId, reactions }) => {
    setMessages(prev => prev.map(msg =>
      msg._id === messageId ? { ...msg, reactions } : msg
    ));
  };

  const handleMessageDeleted = ({ messageId, deleteForEveryone }) => {
    if (deleteForEveryone) {
      setMessages(prev => prev.map(msg =>
        msg._id === messageId
          ? { ...msg, deleted: { isDeleted: true, deletedAt: new Date() } }
          : msg
      ));
      // Keep the chat list preview in sync: if the deleted message is the
      // conversation's lastMessage, mark it deleted so the preview shows
      // "This message was deleted" (WhatsApp-like behavior).
      setConversations(prev => prev.map(conv => (
        conv.lastMessage && conv.lastMessage._id === messageId
          ? {
              ...conv,
              lastMessage: {
                ...conv.lastMessage,
                deleted: {
                  ...(conv.lastMessage.deleted || {}),
                  isDeleted: true,
                  deletedAt: new Date(),
                },
              },
            }
          : conv
      )));
    } else {
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
    }
  };

  const handleMessageEdited = ({ conversationId, message }) => {
    if (selectedConversation && selectedConversation._id === conversationId) {
      setMessages(prev => prev.map(msg => (
        msg._id === message._id ? { ...msg, ...message } : msg
      )));
    }
    upsertConversationInList(conversationId, message);
  };

  const handleMessagesRead = ({ conversationId, readBy }) => {
    if (selectedConversation && selectedConversation._id === conversationId) {
      setMessages(prev => prev.map(msg => ({
        ...msg,
        read: msg.sender._id === user?.id
          ? [...(msg.read || []), { user: readBy, readAt: new Date() }]
          : msg.read
      })));
    }

    // Reset group unread count if this is a group conversation
    const conversation = conversations.find(conv => conv._id === conversationId);
    if (conversation && conversation.type === 'group') {
      const group = groups.find(g => g.name === conversation.name);
      if (group) {
        setGroupUnreadCounts(prev => ({
          ...prev,
          [group.id]: 0
        }));
      }
    }
    // Also reset unread count for the conversation in the conversations list
    setConversations(prev => prev.map(conv => conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv));
  };

  const handleUserTyping = ({ conversationId, userId, isTyping }) => {
    if (selectedConversation && selectedConversation._id === conversationId) {
      setTypingUsers(prev => ({
        ...prev,
        [conversationId]: {
          ...prev[conversationId],
          [userId]: isTyping
        }
      }));
    }
  };

  const handleIncomingCall = ({ call, caller }) => {
    setIncomingCall({ call, caller });
  };

  const handleCallAnswered = ({ callId, answeredBy, webrtcRoom }) => {
    if (currentCall && currentCall._id === callId) {
      setCurrentCall({ ...currentCall, status: 'active', webrtcRoom });
    }
    // Also handle outgoing call being answered
    if (outgoingCall && outgoingCall.call._id === callId) {
      setCurrentCall({ ...outgoingCall.call, status: 'active', webrtcRoom });
      setOutgoingCall(null);
    }
  };

  const handleCallDeclined = ({ callId }) => {
    if (currentCall && currentCall._id === callId) {
      setCurrentCall(null);
    }
    if (incomingCall && incomingCall.call._id === callId) {
      setIncomingCall(null);
    }
    // Handle outgoing call being declined
    if (outgoingCall && outgoingCall.call._id === callId) {
      setOutgoingCall(null);
      alert("Call was declined by the recipient.");
    }
  };

  const handleCallEnded = ({ callId }) => {
    if (currentCall && currentCall._id === callId) {
      setCurrentCall(null);
    }
    // Handle outgoing call being ended
    if (outgoingCall && outgoingCall.call._id === callId) {
      setOutgoingCall(null);
    }
  };

  const handleCallCanceled = ({ callId }) => {
    if (incomingCall && incomingCall.call._id === callId) {
      setIncomingCall(null);
    }
    // Handle outgoing call being canceled
    if (outgoingCall && outgoingCall.call._id === callId) {
      setOutgoingCall(null);
    }
  };

  const handleUserOnline = ({ userId }) => {
    const normalizedUserId = String(userId);
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      newSet.add(normalizedUserId);
      // console.log('User came online:', normalizedUserId, 'Online users:', Array.from(newSet));
      return newSet;
    });
  };

  const handleUserOffline = ({ userId }) => {
    const normalizedUserId = String(userId);
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(normalizedUserId);
      // Also try deleting with original format in case it was stored differently
      if (userId !== normalizedUserId) {
        newSet.delete(userId);
      }
      // console.log('User went offline:', normalizedUserId, 'Online users:', Array.from(newSet));
      return newSet;
    });
  };

  const handleOnlineUsersList = ({ userIds }) => {
    // Normalize all user IDs to strings
    const normalizedUserIds = userIds.map(id => String(id));
    setOnlineUsers(new Set(normalizedUserIds));
    // console.log('Received online users list:', normalizedUserIds);
  };

  // Soft "leave group" — mark the conversation as read-only locally so the
  // chat history stays visible while the message input is disabled. Also
  // remove the group from the Groups tab since the user is no longer part
  // of the underlying CustomList.
  const handleGroupLeft = (conversationId, options = {}) => {
    const leftAt = options.leftAt || new Date().toISOString();
    const transferredAdminId = options.newAdmin
      ? String(options.newAdmin?._id || options.newAdmin)
      : "";
    const userIdStr = String(user?.id || "");

    let leftConversationName = null;

    const markLeftLocally = (conv) => {
      if (!conv || conv._id !== conversationId) return conv;
      if (!leftConversationName && conv.name) leftConversationName = conv.name;
      const existingLeft = Array.isArray(conv.leftMembers) ? conv.leftMembers : [];
      const alreadyTracked = existingLeft.some(
        (entry) => String(entry?.user?._id || entry?.user || "") === userIdStr
      );
      const nextLeftMembers = alreadyTracked
        ? existingLeft
        : [...existingLeft, { user: userIdStr, leftAt }];
      const nextParticipants = (conv.participants || []).filter(
        (p) => String(p?._id || p?.id || p) !== userIdStr
      );
      return {
        ...conv,
        userLeft: true,
        leftAt,
        leftMembers: nextLeftMembers,
        participants: nextParticipants,
        unreadCount: 0,
        // Keep the successor admin on the conversation so Group Info can
        // label them even after this user has left.
        ...(transferredAdminId ? { admin: transferredAdminId } : {}),
      };
    };

    setConversations((prev) => prev.map(markLeftLocally));

    if (selectedConversation && selectedConversation._id === conversationId) {
      setSelectedConversation((prev) => markLeftLocally(prev));
    }

    // Remove from Groups tab — the user is no longer a CustomList member.
    if (leftConversationName) {
      let removedGroupId = null;
      setGroups((prev) => {
        const gone = prev.find((g) => g.name === leftConversationName);
        if (gone) removedGroupId = gone.id;
        return prev.filter((g) => g.name !== leftConversationName);
      });
      if (removedGroupId) {
        setGroupUnreadCounts((prev) => {
          if (!(removedGroupId in prev)) return prev;
          const next = { ...prev };
          delete next[removedGroupId];
          return next;
        });
        setGroupLastActivity((prev) => {
          if (!(removedGroupId in prev)) return prev;
          const next = { ...prev };
          delete next[removedGroupId];
          return next;
        });
      }
    }
  };

  // Hard-delete a group conversation from this user's view (after they have
  // already left it). The backend has dropped them from leftMembers, so the
  // conversation will no longer come back through getConversations.
  const handleGroupDeleted = (conversationId) => {
    setConversations((prev) => prev.filter((conv) => conv._id !== conversationId));
    if (selectedConversation && selectedConversation._id === conversationId) {
      setSelectedConversation(null);
      setMessages([]);
      try {
        sessionStorage.removeItem("messenger_active_conversation_id");
        window.activeConversationId = null;
        window.__messengerChatOpen = false;
      } catch (e) { /* ignore */ }
    }
  };

  const handleGroupMemberAdded = ({ conversationId, userId: addedUserId }) => {
    if (!conversationId || !addedUserId) return;
    const userIdStr = String(addedUserId);

    const ensureParticipant = (conv) => {
      if (!conv || conv._id !== conversationId) return conv;
      const participants = Array.isArray(conv.participants) ? conv.participants : [];
      if (participants.some((p) => String(p?._id || p?.id || p) === userIdStr)) {
        return conv;
      }
      return { ...conv, participants: [...participants, { _id: userIdStr }] };
    };

    setConversations((prev) => prev.map(ensureParticipant));
    if (selectedConversation && selectedConversation._id === conversationId) {
      setSelectedConversation((prev) => ensureParticipant(prev));
    }
  };

  const handleGroupMemberLeft = ({
    conversationId,
    userId: leftUserId,
    leftAt,
    isSelf,
    newAdmin,
  }) => {
    if (isSelf || String(leftUserId) === String(user?.id)) {
      // Admin-transfer leave is the same as a regular leave for the leaver:
      // keep the chat read-only so they can still open/delete it.
      handleGroupLeft(conversationId, { leftAt, newAdmin });
      return;
    }

    const applyLeaveLocally = (conv) => {
      if (!conv) return conv;
      const filtered = (conv.participants || []).filter(
        (p) => String(p._id || p) !== String(leftUserId)
      );
      const next = { ...conv, participants: filtered };
      if (newAdmin) {
        next.admin = newAdmin;
      }
      return next;
    };

    setConversations((prev) =>
      prev.map((conv) =>
        conv._id === conversationId ? applyLeaveLocally(conv) : conv
      )
    );

    if (selectedConversation && selectedConversation._id === conversationId) {
      setSelectedConversation((prev) => applyLeaveLocally(prev));
    }
  };

  // An admin removed a member. If the removed user is us, keep the group in the
  // Groups tab as a read-only "left" entry (via handleGroupLeft) — it must not
  // appear in the one-to-one Chats list. For everyone else, just drop the
  // removed participant so the member list stays accurate.
  const handleGroupMemberRemoved = ({
    conversationId,
    userId: removedUserId,
    leftAt,
    isSelf,
    systemMessage,
  }) => {
    if (isSelf || String(removedUserId) === String(user?.id)) {
      if (
        systemMessage &&
        selectedConversation &&
        selectedConversation._id === conversationId
      ) {
        setMessages((prev) => {
          if (prev.some((m) => m && m._id === systemMessage._id)) return prev;
          return [...prev, systemMessage];
        });
      }
      handleGroupLeft(conversationId, { leftAt });
      return;
    }

    const dropParticipant = (conv) => {
      if (!conv || conv._id !== conversationId) return conv;
      const filtered = (conv.participants || []).filter(
        (p) => String(p._id || p) !== String(removedUserId)
      );
      return { ...conv, participants: filtered };
    };

    setConversations((prev) => prev.map(dropParticipant));
    if (selectedConversation && selectedConversation._id === conversationId) {
      setSelectedConversation((prev) => dropParticipant(prev));
    }
  };

  // The owner disbanded the whole group: it no longer exists for anyone.
  // Remove it from the Groups tab and drop the conversation entirely.
  const handleGroupDisbanded = ({ conversationId, groupName }) => {
    if (!conversationId) return;

    let removedGroupName = groupName || null;
    setConversations((prev) =>
      prev.filter((conv) => {
        if (conv._id === conversationId) {
          if (!removedGroupName && conv.name) removedGroupName = conv.name;
          return false;
        }
        return true;
      })
    );

    if (removedGroupName) {
      let removedGroupId = null;
      setGroups((prev) => {
        const gone = prev.find((g) => g.name === removedGroupName);
        if (gone) removedGroupId = gone.id;
        return prev.filter((g) => g.name !== removedGroupName);
      });
      if (removedGroupId) {
        setGroupUnreadCounts((prev) => {
          if (!(removedGroupId in prev)) return prev;
          const next = { ...prev };
          delete next[removedGroupId];
          return next;
        });
        setGroupLastActivity((prev) => {
          if (!(removedGroupId in prev)) return prev;
          const next = { ...prev };
          delete next[removedGroupId];
          return next;
        });
      }
    }

    if (groupToDelete && groupToDelete.conversationId === conversationId) {
      setGroupToDelete(null);
    }

    if (selectedConversation && selectedConversation._id === conversationId) {
      setSelectedConversation(null);
      setMessages([]);
      try {
        sessionStorage.removeItem("messenger_active_conversation_id");
        window.activeConversationId = null;
        window.__messengerChatOpen = false;
      } catch (e) { /* ignore */ }
    }

    try {
      const label = removedGroupName ? `"${removedGroupName}"` : 'The group';
      const notification = document.createElement('div');
      notification.textContent = `${label} has been disbanded`;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #adb5bd;
        color: #fff;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10001;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } catch (e) { /* ignore */ }
  };

  // Handle ending outgoing call
  const handleEndOutgoingCall = async () => {
    if (!outgoingCall) return;

    try {
      if (outgoingCall.call._id !== 'temp-outgoing-call') {
        await messengerService.endCall(outgoingCall.call._id, user?.token);
      }
      setOutgoingCall(null);
    } catch (error) {
      console.error("Error ending outgoing call:", error);
      setOutgoingCall(null);
    }
  };

  const handleStartCall = async (type) => {
    try {
      // Get the other participant (recipient)
      const recipient = selectedConversation.participants.find(p => p._id !== user?.id);
      if (!recipient) {
        console.error("❌ No recipient found in conversation");
        alert("Error: No recipient found in conversation.");
        return;
      }

      // Show outgoing call modal immediately
      setOutgoingCall({
        call: {
          _id: 'temp-outgoing-call',
          type: type,
          status: 'calling'
        },
        recipient: recipient
      });

      const response = await messengerService.initiateCall({
        conversationId: selectedConversation._id,
        type
      }, user?.token);

      // Update outgoing call with real call data
      setOutgoingCall({
        call: response.call,
        recipient: recipient
      });

      // The call will transition to currentCall when answered
    } catch (error) {
      console.error("Error starting call:", error);

      // Hide outgoing call modal on error
      setOutgoingCall(null);

      // Handle stuck call error specifically
      if (error.message && error.message.includes("already an active call")) {
        console.log("🧹 Detected stuck call, attempting cleanup...");

        try {
          // Clean up stuck calls
          const cleanupResult = await messengerService.cleanupStuckCalls(selectedConversation._id, false, user?.token);
          console.log("🧹 Cleanup result:", cleanupResult);

          // Wait a moment for cleanup to complete
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Try to start the call again
          const retryResponse = await messengerService.initiateCall({
            conversationId: selectedConversation._id,
            type
          }, user?.token);

          // Get the other participant (recipient)
          const recipient = selectedConversation.participants.find(p => p._id !== user?.id);

          // Update outgoing call with real call data
          setOutgoingCall({
            call: retryResponse.call,
            recipient: recipient
          });

          return;
        } catch (cleanupError) {
          console.error("❌ Failed to cleanup or retry call:", cleanupError);

          // Show user-friendly error with manual cleanup option
          const shouldForceCleanup = window.confirm(
            `There's an active call blocking this conversation. The automatic cleanup failed.\n\n` +
            `Would you like to force clean up all stuck calls and try again?`
          );

          if (shouldForceCleanup) {
            try {
              console.log("🧹 Force cleaning up stuck calls...");
              await messengerService.cleanupStuckCalls(selectedConversation._id, true, user?.token);

              // Wait a moment for cleanup to complete
              await new Promise(resolve => setTimeout(resolve, 1000));

              // Try to start the call again
              const retryResponse = await messengerService.initiateCall({
                conversationId: selectedConversation._id,
                type
              }, user?.token);

              // Get the other participant (recipient)
              const recipient = selectedConversation.participants.find(p => p._id !== user?.id);

              // Update outgoing call with real call data
              setOutgoingCall({
                call: retryResponse.call,
                recipient: recipient
              });

              return;
            } catch (forceCleanupError) {
              console.error("❌ Force cleanup also failed:", forceCleanupError);
              alert(`Failed to start ${type} call: Unable to clean up stuck calls. Please try again later.`);
            }
          }
        }
      } else {
        alert(`Failed to start ${type} call: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleAnswerCall = async () => {
    try {
      const response = await messengerService.answerCall(incomingCall.call._id, user?.token);
      setCurrentCall(response.call);
      setIncomingCall(null);
    } catch (error) {
      console.error("Error answering call:", error);
    }
  };

  const handleDeclineCall = async () => {
    try {
      await messengerService.declineCall(incomingCall.call._id, user?.token);
      setIncomingCall(null);
    } catch (error) {
      console.error("Error declining call:", error);
    }
  };

  const handleEndCall = async () => {
    try {
      await messengerService.endCall(currentCall._id, user?.token);
      setCurrentCall(null);
    } catch (error) {
      console.error("Error ending call:", error);
    }
  };

  const handleStartNewConversation = async (participantId) => {
    try {
      const conversation = await messengerService.getOrCreateConversation({
        participantId,
        type: 'private'
      }, user?.token);

      // Always show Chats when opening a 1:1 from profile Message (may have
      // been on the Groups tab after viewing a group member profile).
      setActiveTab('chats');
      setHideMessengerWhileOnProfile(false);
      setIsProfileTransitioning(false);

      const existingInList = conversations.find((conv) => conv._id === conversation._id);
      if (existingInList) {
        await handleConversationSelect(existingInList);
        return;
      }

      const messagesData = await messengerService.getMessages(conversation._id, user?.token);

      if (messagesData.length > 0) {
        const conversationsData = await messengerService.getConversations(user?.token);
        setConversations(conversationsData);
        const refreshedConversation =
          conversationsData.find((conv) => conv._id === conversation._id) || conversation;
        await handleConversationSelect(refreshedConversation);
        return;
      }

      setSelectedConversation(conversation);
      setMessages([]);
      window.activeConversationId = conversation._id;
      try {
        window.__messengerChatOpen = true;
        sessionStorage.setItem('messenger_active_conversation_id', conversation._id);
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error("Error starting new conversation:", error);
    }
  };

  const handleGroupSelect = async (group) => {
    try {
      // console.log("Selected group:", group);

      // Ensure we have the required group data
      if (!group.id || !group.name) {
        console.error("Invalid group data:", group);
        return;
      }

      // Extract member IDs from the populated member objects
      const memberIds = group.members ? group.members.map(member => {
        const memberId = member._id || member.id;
        // console.log("Processing member:", { member, memberId });
        return memberId;
      }).filter(Boolean) : [];

      // Add the group owner to the participants if not already included
      const ownerId = group.owner?._id || group.owner?.id || group.owner;
      if (ownerId && !memberIds.includes(ownerId)) {
        memberIds.push(ownerId);
        // console.log("Added group owner to participants:", ownerId);
      }

      // console.log("Group member IDs (including owner):", memberIds);
      // console.log("Current user ID:", user.id);

      // Create or get group conversation
      const conversation = await messengerService.getOrCreateConversation({
        groupId: group.id,
        type: 'group',
        name: group.name,
        participants: memberIds
      }, user?.token);

      // console.log("Group conversation created:", conversation);

      // Open the group chat without adding empty threads to the sidebar list.
      const existingInList = conversations.find((conv) => conv._id === conversation._id);
      let activeConversation = existingInList || conversation;

      if (!existingInList) {
        const messagesData = await messengerService.getMessages(conversation._id, user?.token);
        if (messagesData.length > 0) {
          const conversationsData = await messengerService.getConversations(user?.token);
          setConversations(conversationsData);
          activeConversation =
            conversationsData.find((conv) => conv._id === conversation._id) || conversation;
        }
        setMessages(messagesData);
      }

      setSelectedConversation(activeConversation);
      // Keep the Groups tab active so back returns to the group list, not Chats.
      setActiveTab('groups');
      window.activeConversationId = activeConversation._id;
      try {
        window.__messengerChatOpen = true;
        sessionStorage.setItem('messenger_active_conversation_id', activeConversation._id);
      } catch (e) { /* ignore */ }

      try {
        if (existingInList) {
          const messagesData = await messengerService.getMessages(conversation._id, user?.token);
          setMessages(messagesData);
        }

        await messengerService.markAsRead(conversation._id, user?.token);
        setGroupUnreadCounts((prev) => ({
          ...prev,
          [group.id]: 0,
        }));
        // Note: do NOT bump groupLastActivity here. Simply opening a group must
        // not move it to the top of the list. Its position stays chronological
        // (by last message timestamp) unless it has unread messages.
      } catch (error) {
        console.error("Error loading group messages:", error);
      }

    } catch (error) {
      console.error("Error starting group conversation:", error);
      // After leaving (e.g. admin-transfer), getOrCreateConversation returns 403
      // because the user is no longer a CustomList member. Drop the stale Groups
      // tab entry so it doesn't stay stuck as a non-openable row.
      const status = error?.response?.status || error?.status;
      const msg = String(error?.message || "").toLowerCase();
      if (status === 403 || msg.includes("no longer a member")) {
        setGroups((prev) => prev.filter((g) => g.id !== group.id && g.name !== group.name));
        return;
      }
      alert("Failed to start group conversation. Please try again.");
    }
  };

  // Open a group the user has already left/been removed from. We only open the
  // existing (read-only) conversation and never call getOrCreateConversation,
  // so the backend cannot silently re-add the user as a participant.
  const handleLeftGroupSelect = async (group) => {
    const conversation = conversations.find(
      (conv) => conv._id === group.conversationId
    );
    if (!conversation) return;

    try {
      setSelectedConversation(conversation);
      setActiveTab('groups');
      window.activeConversationId = conversation._id;
      try {
        window.__messengerChatOpen = true;
        sessionStorage.setItem('messenger_active_conversation_id', conversation._id);
      } catch (e) { /* ignore */ }

      const messagesData = await messengerService.getMessages(
        conversation._id,
        user?.token
      );
      setMessages(messagesData);
    } catch (error) {
      console.error("Error opening left group:", error);
    }
  };

  // Confirmed "Delete Group" from the Groups list. This removes the left group
  // only from the Groups section (via deleteConversation, which drops the user
  // from the conversation's leftMembers on the backend). It is never handled as
  // a one-to-one chat deletion.
  const handleDeleteLeftGroup = async () => {
    if (!groupToDelete || isDeletingLeftGroup) return;
    const conversationId = groupToDelete.conversationId;

    try {
      setIsDeletingLeftGroup(true);
      if (conversationId) {
        await messengerService.deleteConversation(conversationId, user?.token);
      }

      setConversations((prev) =>
        prev.filter((conv) => conv._id !== conversationId)
      );

      if (selectedConversation && selectedConversation._id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
        try {
          sessionStorage.removeItem("messenger_active_conversation_id");
          window.activeConversationId = null;
          window.__messengerChatOpen = false;
        } catch (e) { /* ignore */ }
      }

      setGroupToDelete(null);
    } catch (error) {
      console.error("Error deleting group:", error);
      const notification = document.createElement('div');
      notification.textContent =
        error?.message || 'Failed to delete group. Please try again.';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff6b6b;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10001;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } finally {
      setIsDeletingLeftGroup(false);
    }
  };

  const handleClearMessages = (conversationId, preserveMessageIds = []) => {
    // Clear messages for the specific conversation
    if (selectedConversation && selectedConversation._id === conversationId) {
      if (preserveMessageIds.length > 0) {
        setMessages((prev) => prev.filter((msg) => preserveMessageIds.includes(msg._id)));
      } else {
        setMessages([]);
      }
    }

    // You would typically also make an API call here to clear messages on the server
    // await messengerService.clearConversationMessages(conversationId, user.token);
  };

  const handleGroupContextMenu = (event, group, groupConversation) => {
    event.preventDefault();
    
    if (!groupConversation) {
      console.log('No conversation found for group:', group.name);
      return;
    }

    const isPinned = groupConversation.pinned || false;
    
    // Create context menu
    const contextMenu = document.createElement('div');
    contextMenu.className = 'group_context_menu';
    contextMenu.style.cssText = `
      position: fixed;
      top: ${event.clientY}px;
      left: ${event.clientX}px;
      background: var(--bg-primary);
      border: 1px solid var(--border-primary);
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      min-width: 150px;
      overflow: hidden;
    `;
    
    const pinOption = document.createElement('button');
    pinOption.className = 'context_menu_item';
    pinOption.style.cssText = `
      width: 100%;
      padding: 12px 16px;
      border: none;
      background: transparent;
      color: var(--color-primary);
      font-size: 14px;
      text-align: left;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 12px;
      transition: background-color 0.2s ease;
    `;
    pinOption.innerHTML = `
      <span style="font-size: 16px;">${isPinned ? '📌' : '📌'}</span>
      ${isPinned ? 'Unpin Group' : 'Pin Group'}
    `;
    
    pinOption.addEventListener('click', async () => {
      try {
        if (isPinned) {
          await messengerService.unpinConversation(groupConversation._id, user.token);
          console.log('Group unpinned:', group.name);
        } else {
          await messengerService.pinConversation(groupConversation._id, user.token);
          console.log('Group pinned:', group.name);
        }
        
        // Update conversations list to reflect pin status change
        setConversations(prev => prev.map(conv => 
          conv._id === groupConversation._id 
            ? { ...conv, pinned: !isPinned }
            : conv
        ));
        
        // Show notification
        const notification = document.createElement('div');
        notification.textContent = `Group ${isPinned ? 'unpinned' : 'pinned'} successfully`;
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${isPinned ? '#51cf66' : '#ffd43b'};
          color: ${isPinned ? 'white' : '#000'};
          padding: 12px 20px;
          border-radius: 8px;
          z-index: 10001;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        `;
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
        
      } catch (error) {
        console.error('Error toggling pin status:', error);
        
        // Show error notification
        const errorNotification = document.createElement('div');
        errorNotification.textContent = 'Failed to update pin status. Please try again.';
        errorNotification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #ff6b6b;
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          z-index: 10001;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        `;
        document.body.appendChild(errorNotification);
        setTimeout(() => errorNotification.remove(), 3000);
      }
      
      contextMenu.remove();
    });
    
    pinOption.addEventListener('mouseenter', () => {
      pinOption.style.background = 'var(--bg-secondary)';
    });
    
    pinOption.addEventListener('mouseleave', () => {
      pinOption.style.background = 'transparent';
    });
    
    contextMenu.appendChild(pinOption);
    document.body.appendChild(contextMenu);
    
    // Remove context menu when clicking outside
    const removeContextMenu = (e) => {
      if (!contextMenu.contains(e.target)) {
        contextMenu.remove();
        document.removeEventListener('click', removeContextMenu);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', removeContextMenu);
    }, 100);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const upsertConversationInList = async (conversationId, lastMessage) => {
    let needsRefresh = false;

    setConversations((prev) => {
      const existing = prev.find((conv) => conv._id === conversationId);
      if (existing) {
        return prev
          .map((conv) =>
            conv._id === conversationId
              ? { ...conv, lastMessage, lastActivity: new Date() }
              : conv
          )
          .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
      }

      if (selectedConversation?._id === conversationId) {
        return [
          {
            ...selectedConversation,
            lastMessage,
            lastActivity: new Date(),
            unreadCount: 0,
          },
          ...prev,
        ].sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
      }

      needsRefresh = true;
      return prev;
    });

    if (needsRefresh) {
      try {
        const conversationsData = await messengerService.getConversations(user?.token);
        setConversations(conversationsData);
      } catch (error) {
        console.error("Error refreshing conversations after new message:", error);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleMessengerBack = useCallback(() => {
    if (showStarredMessages) {
      setShowStarredMessages(false);
      setShowStarredMoreMenu(false);
      return;
    }
    if (showBlockedUsers) {
      setShowBlockedUsers(false);
      return;
    }
    if (showArchivedConversations) {
      setShowArchivedConversations(false);
      return;
    }
    if (selectedConversation) {
      const wasGroupChat = selectedConversation.type === 'group';
      try {
        sessionStorage.removeItem("messenger_active_conversation_id");
        window.__messengerChatOpen = false;
      } catch (e) { /* ignore */ }
      setPendingHighlightMessageId(null);
      setSelectedConversation(null);
      if (wasGroupChat) {
        setActiveTab('groups');
      }
      return;
    }

    if (isOpen) {
      onClose();
    }
  }, [
    showStarredMessages,
    showBlockedUsers,
    showArchivedConversations,
    selectedConversation,
    isOpen,
    onClose,
  ]);

  // Sync messenger chat state for mobile hardware-back handling
  useEffect(() => {
    if (!isOpen) return;

    if (selectedConversation?._id) {
      window.activeConversationId = selectedConversation._id;
      window.__messengerChatOpen = true;
      try {
        sessionStorage.setItem('messenger_active_conversation_id', selectedConversation._id);
      } catch (e) { /* ignore */ }
      return;
    }

    window.activeConversationId = null;
    window.__messengerChatOpen = false;
    try {
      sessionStorage.removeItem('messenger_active_conversation_id');
    } catch (e) { /* ignore */ }
  }, [isOpen, selectedConversation?._id]);

  useEffect(() => {
    const onHardwareBack = () => {
      if (!isOpen) return;
      handleMessengerBack();
    };

    window.addEventListener("messengerHardwareBack", onHardwareBack);
    return () => window.removeEventListener("messengerHardwareBack", onHardwareBack);
  }, [handleMessengerBack, isOpen]);

  const handleOpenProfileFromChat = ({ username }) => {
    if (!username) return;

    try {
      const activeConversationId = selectedConversation?._id;
      const scroller = sidebarContainerRef.current?.querySelector('.messages_container');
      const currentScrollTop = scroller ? scroller.scrollTop : 0;
      if (activeConversationId) {
        sessionStorage.setItem('messenger_active_conversation_id', activeConversationId);
        sessionStorage.setItem(`messenger_scroll_${activeConversationId}`, String(currentScrollTop));
      }
    } catch (e) {
      // ignore session storage errors
    }

    setIsProfileTransitioning(true);
    setHideMessengerWhileOnProfile(true);
    setTimeout(() => {
      navigate(`/profile/${username}`);
    }, 180);
  };

  // Handle archived conversations
  // eslint-disable-next-line no-unused-vars
  const handleShowArchivedConversations = async () => {
    try {
      const response = await messengerService.getArchivedConversations(user?.token);
      // Handle different possible response structures
      const archived = Array.isArray(response) ? response : (response?.data || response?.conversations || []);
      setArchivedConversations(archived);
      setShowStarredMessages(false);
      setShowArchivedConversations(true);
      setShowMoreMenu(false);
    } catch (error) {
      console.error("Error fetching archived conversations:", error);
      // Set empty array on error to prevent map error
      setArchivedConversations([]);
      setShowStarredMessages(false);
      setShowArchivedConversations(true);
      setShowMoreMenu(false);
      alert("Failed to load archived conversations");
    }
  };

  const handleUnarchiveConversation = async (conversationId) => {
    try {
      await messengerService.unarchiveConversation(conversationId, user?.token);
      // Remove from archived list
      setArchivedConversations(prev => prev.filter(conv => conv._id !== conversationId));
      // Refresh main conversations list
      const conversationsData = await messengerService.getConversations(user?.token);
      setConversations(conversationsData);
    } catch (error) {
      console.error("Error unarchiving conversation:", error);
      alert("Failed to unarchive conversation");
    }
  };

  const getStarredMessagesFromStorage = () => {
    try {
      const store = JSON.parse(localStorage.getItem('starredMessagesData') || '{}');
      const list = Object.values(store || {}).filter(Boolean);
      return list.sort((a, b) => new Date(b.starredAt || b.createdAt || 0) - new Date(a.starredAt || a.createdAt || 0));
    } catch {
      return [];
    }
  };

  const handleShowStarredMessages = () => {
    setStarredMessages(getStarredMessagesFromStorage());
    setShowArchivedConversations(false);
    setShowBlockedUsers(false);
    setShowStarredMessages(true);
    setShowMoreMenu(false);
  };

  const handleShowBlockedUsers = async () => {
    try {
      const latestBlocked = await messengerService.getBlockedUsers(user?.token);
      setBlockedUsers(Array.isArray(latestBlocked) ? latestBlocked : []);
    } catch (error) {
      console.error("Error loading blocked users:", error);
      setBlockedUsers([]);
    }
    setShowArchivedConversations(false);
    setShowStarredMessages(false);
    setShowBlockedUsers(true);
    setShowMoreMenu(false);
  };

  // const handleOpenMessengerSettings = () => {
  //   setShowMoreMenu(false);
  //   alert("Messenger Settings are coming soon.");
  // };

  const handleOpenStarredMessage = async (starredItem) => {
    try {
      if (!starredItem?.conversationId || !starredItem?.messageId) return;
      let conversation = conversations.find((conv) => conv._id === starredItem.conversationId);
      if (!conversation) {
        const refreshed = await messengerService.getConversations(user?.token);
        setConversations(refreshed);
        conversation = refreshed.find((conv) => conv._id === starredItem.conversationId);
      }
      if (!conversation) {
        alert("Original conversation not found.");
        return;
      }
      setPendingHighlightMessageId(starredItem.messageId);
      setShowStarredMessages(false);
      await handleConversationSelect(conversation);
    } catch (error) {
      console.error("Error opening starred message:", error);
      alert("Failed to open starred message.");
    }
  };

  const handleUnstarFromStarred = (messageId) => {
    if (!messageId) return;
    try {
      const id = String(messageId);
      const idStore = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      const dataStore = JSON.parse(localStorage.getItem('starredMessagesData') || '{}');
      delete idStore[id];
      delete dataStore[id];
      localStorage.setItem('starredMessages', JSON.stringify(idStore));
      localStorage.setItem('starredMessagesData', JSON.stringify(dataStore));
      setStarredMessages((prev) => prev.filter((item) => String(item.messageId) !== id));
      window.dispatchEvent(new CustomEvent('starredMessagesUpdated'));
    } catch (error) {
      console.error("Error unstarring from starred list:", error);
    }
  };

  const handleUnstarAllFromStarred = () => {
    try {
      localStorage.setItem('starredMessages', JSON.stringify({}));
      localStorage.setItem('starredMessagesData', JSON.stringify({}));
      setStarredMessages([]);
      setShowStarredMoreMenu(false);
      window.dispatchEvent(new CustomEvent('starredMessagesUpdated'));
    } catch (error) {
      console.error("Error unstarring all messages:", error);
    }
  };

  const handleUnblockFromMessenger = async (blockedUserId) => {
    try {
      await messengerService.unblockUser(blockedUserId, user?.token);
      setBlockedUsers((prev) => prev.filter((item) => String(item._id) !== String(blockedUserId)));
      window.dispatchEvent(new CustomEvent('blockedUsersUpdated'));

      // Refresh conversations so blocked chats can immediately restore identity + messaging state
      const conversationsData = await messengerService.getConversations(user?.token);
      setConversations(conversationsData);
      if (selectedConversation && selectedConversation._id) {
        const refreshed = conversationsData.find((conv) => conv._id === selectedConversation._id);
        if (refreshed) {
          setSelectedConversation(refreshed);
          const msgs = await messengerService.getMessages(refreshed._id, user?.token);
          setMessages(msgs);
        }
      }
    } catch (error) {
      console.error("Error unblocking from messenger:", error);
      alert("Failed to unblock user.");
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
      if (starredMoreMenuRef.current && !starredMoreMenuRef.current.contains(event.target)) {
        setShowStarredMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const selectedConversationId = selectedConversation?._id;
    const syncBlockedUsers = async () => {
      if (!isOpen || !user?.token) return;
      try {
        const latestBlocked = await messengerService.getBlockedUsers(user?.token);
        setBlockedUsers(Array.isArray(latestBlocked) ? latestBlocked : []);
        const refreshedConversations = await messengerService.getConversations(user?.token);
        setConversations(refreshedConversations);
        if (selectedConversationId) {
          const refreshedSelected = refreshedConversations.find((conv) => conv._id === selectedConversationId);
          if (refreshedSelected) {
            setSelectedConversation(refreshedSelected);
            const refreshedMessages = await messengerService.getMessages(refreshedSelected._id, user?.token);
            setMessages(refreshedMessages);
          }
        }
      } catch {
        setBlockedUsers([]);
      }
    };
    window.addEventListener('blockedUsersUpdated', syncBlockedUsers);
    return () => window.removeEventListener('blockedUsersUpdated', syncBlockedUsers);
  }, [isOpen, user?.token, selectedConversation?._id]);

  // Clear active conversation and refresh-block flag when closed
  useEffect(() => {
    if (!isOpen) {
      window.activeConversationId = null;
      try {
        window.__messengerChatOpen = false;
      } catch (e) { /* ignore */ }
    }
  }, [isOpen]);

  // Early return if user is not available
  if (!user || !user.token) {
    return null;
  }

  if (!isOpen) return null;

  // Groups the user has left or been removed from are no longer returned by
  // getCustomLists, but their (read-only) conversation persists. Surface them
  // in the Groups tab as "left" entries so they never leak into the one-to-one
  // Chats list, and can be removed via the in-list "Delete Group" confirmation.
  const activeGroupNames = new Set(
    groups.map((g) => (g.name || '').trim().toLowerCase())
  );
  const leftGroupEntries = conversations
    .filter((conv) => conv.type === 'group' && conv.userLeft)
    .filter(
      (conv) => !activeGroupNames.has((conv.name || '').trim().toLowerCase())
    )
    .map((conv) => ({
      id: conv.groupId || conv._id,
      name: conv.name || 'Group Chat',
      groupPicture: conv.groupPicture || conv.picture || '',
      userLeft: true,
      conversationId: conv._id,
      updatedAt: conv.lastActivity,
      createdAt: conv.lastActivity,
    }));
  const displayGroups = [...groups, ...leftGroupEntries];

  return (
    <>
      {/* Chat Window */}
      <div
        ref={sidebarContainerRef}
        className={`messenger_sidebar_container ${isFullScreen ? 'messenger_fullscreen' : ''} ${isProfileTransitioning ? 'profile_transitioning' : ''} ${isOnProfileRoute && hideMessengerWhileOnProfile ? 'profile_hidden' : ''}`}
      >
        <div className="messenger_sidebar_header">
          <h2>Messenger</h2>
          <div className="messenger_header_buttons">
            {!showStarredMessages && !selectedConversation && (
              <div className="messenger_more_menu" ref={moreMenuRef}>
              <button
                className="messenger_more_btn"
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                title="More options"
                aria-label="Open messenger options"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </button>
              {showMoreMenu && (
                <div className="messenger_more_dropdown">
                  <button
                    className="messenger_dropdown_item"
                    onClick={handleShowStarredMessages}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                    Starred Messages
                  </button>
                  <button
                    className="messenger_dropdown_item"
                    onClick={handleShowBlockedUsers}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13H7v-2h10v2z" />
                    </svg>
                    Blocked Users
                  </button>
                  {/* <button
                    className="messenger_dropdown_item"
                    onClick={handleOpenMessengerSettings}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.14,12.94a7.43,7.43,0,0,0,.05-.94,7.43,7.43,0,0,0-.05-.94l2.11-1.65a.5.5,0,0,0,.12-.64l-2-3.46a.5.5,0,0,0-.61-.22l-2.49,1a7.28,7.28,0,0,0-1.63-.94l-.38-2.65A.5.5,0,0,0,13.77,2H10.23a.5.5,0,0,0-.49.42L9.36,5.07a7.28,7.28,0,0,0-1.63.94l-2.49-1a.5.5,0,0,0-.61.22l-2,3.46a.5.5,0,0,0,.12.64L4.86,11.06a7.43,7.43,0,0,0-.05.94,7.43,7.43,0,0,0,.05.94L2.75,14.59a.5.5,0,0,0-.12.64l2,3.46a.5.5,0,0,0,.61.22l2.49-1a7.28,7.28,0,0,0,1.63.94l.38,2.65a.5.5,0,0,0,.49.42h3.54a.5.5,0,0,0,.49-.42l.38-2.65a7.28,7.28,0,0,0,1.63-.94l2.49,1a.5.5,0,0,0,.61-.22l2-3.46a.5.5,0,0,0-.12-.64ZM12,15.5A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
                    </svg>
                    Settings
                  </button> */}
                </div>
              )}
              </div>
            )}
            {!isMobile && (
              <button className="messenger_fullscreen_btn" onClick={toggleFullScreen} title={isFullScreen ? "Exit Full Screen" : "Full Screen"}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  {isFullScreen ? (
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                  ) : (
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                  )}
                </svg>
              </button>
            )}
            <button className="messenger_close_btn" onClick={onClose} title="Close Messenger">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="messenger_sidebar_content">
          {showStarredMessages ? (
            <div className="messenger_starred_section">
              <div className="starred_header">
                <button
                  className="starred_back_btn"
                  onClick={() => {
                    setShowStarredMoreMenu(false);
                    setShowStarredMessages(false);
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <h3>Starred Messages</h3>
                <div className="starred_header_actions">
                  <div className="messenger_more_menu" ref={starredMoreMenuRef}>
                    <button
                      className="messenger_more_btn"
                      onClick={() => setShowStarredMoreMenu((prev) => !prev)}
                      title="Starred options"
                      aria-label="Open starred options"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                      </svg>
                    </button>
                    {showStarredMoreMenu && (
                      <div className="messenger_more_dropdown">
                        <button
                          className="messenger_dropdown_item"
                          onClick={handleUnstarAllFromStarred}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                          Unstar all
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {starredMessages.length > 0 ? (
                <div className="starred_messages_list">
                  {starredMessages.map((item) => (
                    <div key={item.messageId} className="starred_message_item">
                      <div
                        className="starred_message_info"
                        onClick={() => handleOpenStarredMessage(item)}
                      >
                        <div className="starred_message_avatar">
                          <img src={item.senderPicture || '/images/default_profile.png'} alt={item.senderName || 'Sender'} />
                        </div>
                        <div className="starred_message_details">
                          <h4>{item.senderName || 'Unknown User'}</h4>
                          <p>{item.preview || 'Starred message'}</p>
                          <span className="starred_message_time">
                            {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                          </span>
                        </div>
                      </div>
                      <button
                        className="unstar_btn"
                        onClick={() => handleUnstarFromStarred(item.messageId)}
                        title="Unstar message"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no_starred_messages">
                  <div className="no_starred_icon">★</div>
                  <h3>No starred messages</h3>
                  <p>Star messages in chats and they will appear here</p>
                </div>
              )}
            </div>
          ) : showBlockedUsers ? (
            <div className="messenger_blocked_section">
              <div className="blocked_header">
                <button
                  className="blocked_back_btn"
                  onClick={() => setShowBlockedUsers(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <h3>Blocked Users</h3>
              </div>

              {blockedUsers.length > 0 ? (
                <div className="blocked_users_list_messenger">
                  {blockedUsers.map((blockedUser) => (
                    <div key={blockedUser._id} className="blocked_user_item_messenger">
                      <div className="blocked_user_info_messenger">
                        <img
                          src={blockedUser.picture || '/images/default_profile.png'}
                          alt={blockedUser.name || blockedUser.username || 'Blocked user'}
                          className="blocked_user_avatar_messenger"
                        />
                        <div className="blocked_user_meta_messenger">
                          <h4>{blockedUser.name || `${blockedUser.first_name || ''} ${blockedUser.last_name || ''}`.trim() || 'User'}</h4>
                          <p>@{blockedUser.username || 'unknown'}</p>
                        </div>
                      </div>
                      <button
                        className="unblock_btn_messenger"
                        onClick={() => handleUnblockFromMessenger(blockedUser._id)}
                      >
                        Unblock
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no_blocked_users_messenger">
                  <h3>No blocked users</h3>
                  <p>Blocked users will appear here.</p>
                </div>
              )}
            </div>
          ) : showArchivedConversations ? (
            <div className="messenger_archived_section">
              <div className="archived_header">
                <button
                  className="archived_back_btn"
                  onClick={() => setShowArchivedConversations(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <h3>Archived Conversations</h3>
              </div>

              {archivedConversations.length > 0 ? (
                <div className="archived_conversations_list">
                  {archivedConversations.map((conversation) => (
                    <div key={conversation._id} className="archived_conversation_item">
                      <div className="archived_conversation_info" onClick={() => {
                        handleConversationSelect(conversation);
                        setShowArchivedConversations(false);
                      }}>
                        <div className="archived_conversation_avatar">
                          {conversation.type === 'group' ? (
                            <img
                              src="/images/default_group.svg"
                              alt={conversation.name}
                            />
                          ) : (
                            <img
                              src={conversation.participants?.find(p => p._id !== user?.id)?.picture || '/images/default_pic.png'}
                              alt={conversation.participants?.find(p => p._id !== user?.id)?.first_name}
                            />
                          )}
                        </div>
                        <div className="archived_conversation_details">
                          <h4>
                            {conversation.type === 'group'
                              ? conversation.name
                              : conversation.participants?.find(p => p._id !== user?.id)?.first_name + ' ' + conversation.participants?.find(p => p._id !== user?.id)?.last_name
                            }
                            {/* Show indicator for old conversations */}
                            {!conversation.archived?.some(arch => arch.user === user?.id) && (
                              <span className="old_conversation_badge" title="Old conversation (auto-archived)">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
                                </svg>
                              </span>
                            )}
                          </h4>
                          <p>
                            {(() => {
                              const lm = conversation.lastMessage;
                              if (!lm) return 'No messages yet';
                              if (lm.deleted?.isDeleted) return 'This message was deleted';
                              switch (lm.type) {
                                case 'text':
                                  return lm.content?.text || 'No messages yet';
                                case 'image':
                                  return 'Photo';
                                case 'video':
                                  return 'Video';
                                case 'audio':
                                  return 'Voice message';
                                case 'file':
                                  return 'File';
                                case 'call': {
                                  const callType = lm.content?.call?.type || 'voice';
                                  const callStatus = lm.content?.call?.status;
                                  return `${callStatus === 'missed' ? 'Missed' : ''} ${callType} call`.trim();
                                }
                                case 'system':
                                  return lm.content?.text || 'Group updated';
                                default:
                                  return 'New message';
                              }
                            })()}
                            {/* Show age indicator for old conversations */}
                            {!conversation.archived?.some(arch => arch.user === user?.id) && (
                              <span className="conversation_age">
                                {' • '}
                                {(() => {
                                  const lastActivity = new Date(conversation.lastActivity);
                                  const now = new Date();
                                  const diffDays = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));
                                  if (diffDays > 365) {
                                    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
                                  } else if (diffDays > 30) {
                                    return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
                                  } else {
                                    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                                  }
                                })()}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <button
                        className="unarchive_btn"
                        onClick={() => handleUnarchiveConversation(conversation._id)}
                        title="Unarchive conversation"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 9.5l5.5 5.5H14v2h-4v-2H6.5L12 9.5zM5.12 5l.81-1h12l.94 1H5.12z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no_archived_conversations">
                  <div className="no_archived_icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z" />
                    </svg>
                  </div>
                  <h3>No archived conversations</h3>
                  <p>Conversations you archive will appear here</p>
                </div>
              )}
            </div>
          ) : !selectedConversation ? (
            <div className="messenger_conversations_section">
              {/* WhatsApp-like tabs */}
              <div className="messenger_tabs">
                <button
                  className={`messenger_tab ${activeTab === 'chats' ? 'active' : ''}`}
                  onClick={() => setActiveTab('chats')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                  </svg>
                  Chats
                </button>
                <button
                  className={`messenger_tab ${activeTab === 'groups' ? 'active' : ''}`}
                  onClick={() => setActiveTab('groups')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26A1.5 1.5 0 0 0 12.5 12H12v2.5c0 .83.67 1.5 1.5 1.5H15v6h5z" />
                    <path d="M12.5 11.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10s.67 1.5 1.5 1.5zM5.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm2 16v-6H9.9l1.49-3.96A1.5 1.5 0 0 0 8.5 11H8c-.8 0-1.54.37-2.01 1l-1.7 2.26A1.5 1.5 0 0 0 3.5 16H3v2.5c0 .83.67 1.5 1.5 1.5H6v6h1.5z" />
                  </svg>
                  Groups
                </button>
              </div>

              {activeTab === 'chats' ? (
                <ConversationsList
                  conversations={conversations.filter(
                    (conv) => conv.type !== 'group'
                  )}
                  selectedConversation={selectedConversation}
                  onConversationSelect={handleConversationSelect}
                  onStartNewConversation={handleStartNewConversation}
                  currentUser={user}
                  loading={loading}
                  onlineUsers={onlineUsers}
                />
              ) : (
                <div className="messenger_groups_section">
                  <div className="groups_header">
                    <h3>Groups</h3>
                    <div className="groups_header_right">
                      <span className="groups_count">{displayGroups.length} groups</span>
                    </div>
                  </div>

                  {loading ? (
                    <div className="groups_loading">
                      <div className="loading_spinner"></div>
                      <p>Loading groups...</p>
                    </div>
                  ) : displayGroups.length > 0 ? (
                    <div className="groups_list">
                      {/* Sort groups by last activity and unread counts */}
                      {displayGroups
                        .sort((a, b) => {
                          const aUnread = groupUnreadCounts[a.id] || 0;
                          const bUnread = groupUnreadCounts[b.id] || 0;

                          // Comprehensive sorting: pinned first, then unread, then by last activity
                          const aConversation = conversations.find(conv => conv.type === 'group' && conv.name === a.name);
                          const bConversation = conversations.find(conv => conv.type === 'group' && conv.name === b.name);

                          // Chronological position is based on the last message timestamp
                          // (conversation.lastActivity). groupLastActivity only carries
                          // real-time bumps from newly arrived messages, never from simply
                          // opening a group, so opening a read group won't reorder the list.
                          const aLastActivity = groupLastActivity[a.id] || aConversation?.lastActivity || new Date(a.updatedAt || a.createdAt);
                          const bLastActivity = groupLastActivity[b.id] || bConversation?.lastActivity || new Date(b.updatedAt || b.createdAt);

                          const aPinned = aConversation?.pinned || false;
                          const bPinned = bConversation?.pinned || false;
                          
                          // 1. Pinned groups come first
                          if (aPinned && !bPinned) return -1;
                          if (!aPinned && bPinned) return 1;
                          
                          // 2. Within same pin status, groups with unread messages come first
                          if (aUnread > 0 && bUnread === 0) return -1;
                          if (bUnread > 0 && aUnread === 0) return 1;

                          // 3. Finally, sort by last activity (most recent first)
                          return new Date(bLastActivity) - new Date(aLastActivity);
                        })
                        .map((group) => {
                          const hasLeft = !!group.userLeft;
                          const unreadCount = hasLeft ? 0 : (groupUnreadCounts[group.id] || 0);
                          const groupConversation = conversations.find(conv => conv.type === 'group' && conv.name === group.name);
                          const isPinned = !hasLeft && (groupConversation?.pinned || false);
                          
                          return (
                            <div
                              key={group.id}
                              className={`group_item ${unreadCount > 0 ? 'has-unread' : ''} ${isPinned ? 'pinned' : ''} ${hasLeft ? 'group_left' : ''}`}
                              onClick={() =>
                                hasLeft
                                  ? handleLeftGroupSelect(group)
                                  : handleGroupSelect(group)
                              }
                              onContextMenu={(e) => {
                                e.preventDefault();
                                if (hasLeft) return;
                                handleGroupContextMenu(e, group, groupConversation);
                              }}
                            >
                              {isPinned && (
                                <div className="pin_indicator" title="Pinned group">
                                  📌
                                </div>
                              )}
                              <div className="group_picture">
                                <img
                                  src={
                                    group.groupPicture
                                      ? group.groupPicture
                                      : '/images/default_group.svg'
                                  }
                                  alt={group.name}
                                />
                                {unreadCount > 0 && (
                                  <div className="group_unread_badge">
                                    <span className="unread_count">
                                      {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="group_info">
                                <h4 className="group_name">{group.name}</h4>
                                {hasLeft && (
                                  <span className="group_left_label">You left this group</span>
                                )}
                              </div>
                              {hasLeft && (
                                <button
                                  type="button"
                                  className="group_delete_btn"
                                  title="Delete group"
                                  aria-label={`Delete ${group.name}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGroupToDelete(group);
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="no_groups">
                      <div className="no_groups_icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26A1.5 1.5 0 0 0 12.5 12H12v2.5c0 .83.67 1.5 1.5 1.5H15v6h5z" />
                          <path d="M12.5 11.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10s.67 1.5 1.5 1.5zM5.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm2 16v-6H9.9l1.49-3.96A1.5 1.5 0 0 0 8.5 11H8c-.8 0-1.54.37-2.01 1l-1.7 2.26A1.5 1.5 0 0 0 3.5 16H3v2.5c0 .83.67 1.5 1.5 1.5H6v6h1.5z" />
                        </svg>
                      </div>
                      <h3>No groups yet</h3>
                      <p>Create or join groups to start group conversations</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="messenger_chat_section">
              <ChatWindow
                conversation={selectedConversation}
                messages={messages}
                currentUser={user}
                onSendMessage={handleSendMessage}
                onSendMedia={handleSendMedia}
                onEditMessage={handleEditMessage}
                onForwardMessage={handleForwardMessage}
                onStartCall={handleStartCall}
                typingUsers={typingUsers[selectedConversation._id] || {}}
                socket={socket}
                messagesEndRef={messagesEndRef}
                onBack={handleMessengerBack}
                jumpToMessageId={pendingHighlightMessageId}
                onJumpHandled={() => setPendingHighlightMessageId(null)}
                blockedUserIds={(blockedUsers || []).map((b) => String(b._id))}
                onlineUsers={onlineUsers}
                onClearMessages={handleClearMessages}
                onConversationPinToggle={(conversationId, pinned) => {
                  // Update conversations list to reflect pin status change
                  setConversations(prev => prev.map(conv => 
                    conv._id === conversationId 
                      ? { ...conv, pinned: pinned }
                      : conv
                  ));
                  // Also update selected conversation if it's the same one
                  if (selectedConversation && selectedConversation._id === conversationId) {
                    setSelectedConversation(prev => ({ ...prev, pinned: pinned }));
                  }
                }}
                conversations={conversations}
                onOpenProfile={handleOpenProfileFromChat}
                onLeaveGroup={handleGroupLeft}
                onDeleteGroup={handleGroupDeleted}
              />
            </div>
          )}
        </div>

        {/* Video/Voice Call Window */}
        {currentCall && (
          <VideoCallWindow
            call={currentCall}
            currentUser={user}
            onEndCall={handleEndCall}
            socket={socket}
          />
        )}
      </div>

      {/* Incoming Call Modal - moved outside messenger_sidebar_container */}
      {incomingCall && (
        <IncomingCallModal
          call={incomingCall.call}
          caller={incomingCall.caller}
          onAnswer={handleAnswerCall}
          onDecline={handleDeclineCall}
        />
      )}

      {/* Outgoing Call Modal - WhatsApp-style */}
      {outgoingCall && (
        <OutgoingCallModal
          call={outgoingCall.call}
          recipient={outgoingCall.recipient}
          onEndCall={handleEndOutgoingCall}
        />
      )}

      {/* Delete Group confirmation for a left/removed group, shown from the
          Groups list — never as a one-to-one chat delete popup. */}
      <DeleteGroupModal
        conversation={groupToDelete ? { name: groupToDelete.name } : null}
        isVisible={!!groupToDelete}
        onClose={() => {
          if (!isDeletingLeftGroup) setGroupToDelete(null);
        }}
        onConfirm={handleDeleteLeftGroup}
      />
    </>
  );
} 
